export default class TabMenu {
    constructor() {
        this.init()
    }

    /**
     * Tab Menu Element Identifier
     * @type {string}
     */
    TAB_MENU = '.js-tabs'

    /**
     * Tab Menu content Identifier
     * @type {string}
     */
    TAB_CONTENT = '.js-tabs__content'

    /**
     * Tab Menu Navigation Item Identifier
     * @type {string}
     */
    NAVIGATION_ITEM = '.js-tabs__navigation-item'

    init() {
        this.bindEventListenerForTabMenus()
        if (window.location.hash.length > 0) {
            this.handleLocationHash(window.location.hash.substring(1))
        }
        // Add click event for document to have fewer listeners and support dynamically created links
        document.addEventListener('click', (event) => {
            // Check if the clicked target is an anchor link on the same site
            if (event.target.hash && event.target.pathname === window.location.pathname) {
                this.handleLocationHash(event.target.hash.substring(1))
            }
        })
    }

    /**
     * Initialize Tab Menus
     *
     * @method loadTabMenus
     * @returns {undefined}
     */
    bindEventListenerForTabMenus() {
        document.querySelectorAll(this.TAB_MENU).forEach((tabMenu) => {
            tabMenu.querySelectorAll(this.NAVIGATION_ITEM).forEach((navigationItem) => {
                this.addListener(tabMenu, navigationItem)
            })
        })
    }

    /**
     * Load click navigation event
     *
     * @param tabMenu current tab Menu
     * @param navigationItem
     * @method addListener
     * @returns {undefined}
     */
    addListener(tabMenu, navigationItem) {
        navigationItem.addEventListener('click', () => {
            const activeItem = tabMenu.querySelector(this.NAVIGATION_ITEM + '.active')
            const activeContent = tabMenu.querySelector(this.TAB_CONTENT + '.active')
            const targetContent = tabMenu.querySelector(this.TAB_CONTENT + `[data-id="${navigationItem.dataset.id}"]`)

            if (activeItem !== navigationItem) {
                activeItem.classList.remove('active')
                activeContent.classList.remove('active')
                navigationItem.classList.add('active')
                targetContent.classList.add('active')
            }
        })
    }

    /**
     * Anchor link handling
     * @method handleLocationHash
     * @returns {undefined}
     */
    handleLocationHash(hash) {
        let navigationItem = Array.from(document.querySelectorAll(this.NAVIGATION_ITEM)).filter(
            (element) => element.dataset.id === hash
        )

        // If the hash is not in a data id and the hash is not in a tab element, let the browser handle the scroll
        if (
            navigationItem.length === 0 &&
            document.getElementById(hash).closest('[class*=js-tabs__][data-id]') === null
        ) {
            return
        }

        // Find the closest parent with a data id and get the nav item with the corresponding id
        if (navigationItem.length === 0) {
            var dataId = document.getElementById(hash).closest('[class*=js-tabs__][data-id]').dataset.id
            navigationItem[0] = document.querySelector(this.NAVIGATION_ITEM + '[data-id="' + dataId + '"]')
        }

        if (navigationItem.length && this.elementIsVisible(navigationItem[0])) {
            const currentNavItem = navigationItem[0]
            const navigation = currentNavItem.parentElement
            currentNavItem.click()
            this.scrollToTabMenu(navigation)
            if (currentNavItem.previousElementSibling) {
                this.scrollToNavigationItem(navigation, currentNavItem)
            }
        } else if (navigationItem.length && !this.elementIsVisible(navigationItem[0])) {
            // Emulate desktop click event if someone switches from mobile to desktop view
            const currentNavItem = navigationItem[0]
            currentNavItem.click()

            if (!dataId) {
                dataId = hash
            }
            // Scroll to tab-content and not to tab-navigation on mobile devices
            let selectedTab = Array.from(document.querySelectorAll(this.TAB_MENU)[0].childNodes).filter(
                (element) => element.dataset.id === dataId
            )[0]

            this.scrollToTabContent(selectedTab)
        }
    }

    /**
     * Function considers an element to be visible if it consumes space in the document.
     *
     * @param element
     * @returns {boolean}
     */
    elementIsVisible(element) {
        return element.offsetWidth > 0 && element.offsetHeight > 0
    }

    /**
     * Scroll to current Tab menu (based on Anchor link handling)
     *
     * @param target
     * @method scrollToTabMenu
     * @returns {undefined}
     */
    scrollToTabMenu(target) {
        const offsetHeight = target.offsetHeight
        const offsetTop = target.offsetTop
        const position = offsetTop - offsetHeight
        const scrollPaddingTop = this.getScrollPaddingTop(target)
        window.scrollTo({
            top: position - scrollPaddingTop,
            left: 0,
            behavior: 'smooth',
        })
    }

    /**
     * Scroll to current Tab content (based on Anchor link handling)
     *
     * @param target
     * @method scrollToTabContent
     * @returns {undefined}
     */
    scrollToTabContent(target) {
        const position = this.getElemDistanceFromWindowTop(target)
        const scrollPaddingTop = this.getScrollPaddingTop(target)
        window.scrollTo({
            top: position - scrollPaddingTop,
            left: 0,
            behavior: 'smooth',
        })
    }

    /**
     *
     * @param elem
     * @returns {number}
     */
    getElemDistanceFromWindowTop(elem) {
        let location = 0
        if (elem.offsetParent) {
            do {
                location += elem.offsetTop
                elem = elem.offsetParent
            } while (elem)
        }
        return location >= 0 ? location : 0
    }

    /**
     * Scroll to the active Tab Navigation Item  (based on Anchor link handling)
     *
     * @param navigation
     * @param navigationItem
     * @method scrollToNavigationItem
     * @returns {undefined}
     */
    scrollToNavigationItem(navigation, navigationItem) {
        const offsetLeft = navigationItem.offsetLeft - navigation.offsetLeft
        const previousMargin = parseInt(window.getComputedStyle(navigationItem.previousElementSibling).marginRight)
        const marginLeft = parseInt(window.getComputedStyle(navigationItem).marginLeft)
        const position = offsetLeft - (previousMargin + marginLeft)

        navigation.scrollTo({
            top: 0,
            left: position,
            behavior: 'smooth',
        })
    }

    getScrollPaddingTop(target) {
        const scrollPaddingTop = target.closest(this.TAB_MENU).dataset.scrollPaddingTop
        return parseInt(scrollPaddingTop)
    }
}
